import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { readingMsForPrompt, TIMED_DURATIONS_SEC } from '@sb2/shared';
import { api, type QuizQuestion } from '../../api/client';
import { useLocale } from '../../i18n/LocaleContext';
import { useAuth } from '../AuthContext';
import { QuizBoard } from '../components/QuizBoard';
import { ShareRunCard } from '../components/ShareRunCard';

type Phase = 'menu' | 'playing' | 'ended';
type SoloMode = 'solo_timed' | 'solo_lives' | 'solo_timebank' | 'solo_daily';

const DEFAULT_REVEAL_MS = 700;

function parseMode(raw: string | null): SoloMode | null {
  if (
    raw === 'solo_timed' ||
    raw === 'solo_lives' ||
    raw === 'solo_timebank' ||
    raw === 'solo_daily'
  ) {
    return raw;
  }
  if (raw === 'daily') return 'solo_daily';
  return null;
}

export function SoloPage() {
  const { ensureGuest, user } = useAuth();
  const { t, locale } = useLocale();
  const [params] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('menu');
  const [mode, setMode] = useState<SoloMode>(() => parseMode(params.get('mode')) ?? 'solo_timed');
  const [activeMode, setActiveMode] = useState<SoloMode>('solo_timed');
  const [durationSec, setDurationSec] = useState<number>(TIMED_DURATIONS_SEC[0]);
  const [runId, setRunId] = useState<string | null>(null);
  const [question, setQuestion] = useState<QuizQuestion | null>(null);
  const [deadlineAt, setDeadlineAt] = useState<number | null>(null);
  const [answerWindowMs, setAnswerWindowMs] = useState<number | null>(null);
  const [showChoices, setShowChoices] = useState(false);
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [timeouts, setTimeouts] = useState(0);
  const [livesLeft, setLivesLeft] = useState<number | null>(null);
  const [locked, setLocked] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [highlightCorrect, setHighlightCorrect] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [dailyTotal, setDailyTotal] = useState<number | null>(null);
  const [dailyIndex, setDailyIndex] = useState<number | null>(null);
  const [dailyPlayed, setDailyPlayed] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const revealTimer = useRef<number | null>(null);
  const readingTimer = useRef<number | null>(null);
  const inFlight = useRef(false);
  const pendingWindow = useRef<number | null>(null);

  const isFirstRun = (user?.quizGamesPlayed ?? 0) === 0;

  useEffect(() => {
    return () => {
      if (revealTimer.current != null) window.clearTimeout(revealTimer.current);
      if (readingTimer.current != null) window.clearTimeout(readingTimer.current);
    };
  }, []);

  useEffect(() => {
    if (phase !== 'playing') return;
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'menu') return;
    let cancelled = false;
    void (async () => {
      try {
        await ensureGuest();
        const d = await api.dailyStatus();
        if (!cancelled) setDailyPlayed(d.played);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, ensureGuest]);

  const clearReveal = () => {
    setSelected(null);
    setHighlightCorrect(null);
  };

  const finishRun = useCallback(() => {
    clearReveal();
    inFlight.current = false;
    if (readingTimer.current != null) {
      window.clearTimeout(readingTimer.current);
      readingTimer.current = null;
    }
    setPhase('ended');
    setQuestion(null);
    setDeadlineAt(null);
    setAnswerWindowMs(null);
    setShowChoices(false);
    setLocked(false);
  }, []);

  const armAnswers = useCallback(
    async (id: string) => {
      if (readingTimer.current != null) {
        window.clearTimeout(readingTimer.current);
        readingTimer.current = null;
      }
      try {
        const armed = await api.nextSolo(id);
        if (armed.ended || !armed.question) {
          setScore(armed.score);
          setStreak(armed.streak);
          setBestStreak(armed.bestStreak);
          setCorrect(armed.correctCount ?? 0);
          setWrong(armed.wrong ?? 0);
          setTimeouts(armed.timeouts ?? 0);
          setLivesLeft(armed.livesLeft);
          finishRun();
          return;
        }
        setScore(armed.score);
        setStreak(armed.streak);
        setBestStreak(armed.bestStreak);
        setCorrect(armed.correctCount ?? 0);
        setWrong(armed.wrong ?? 0);
        setTimeouts(armed.timeouts ?? 0);
        setLivesLeft(armed.livesLeft);
        setEndsAt(armed.endsAt);
        setDailyTotal(armed.dailyTotal ?? null);
        setDailyIndex(armed.dailyIndex ?? null);
        setAnswerWindowMs(armed.answerWindowMs ?? pendingWindow.current);
        setDeadlineAt(armed.deadlineAt);
        setShowChoices(true);
        inFlight.current = false;
        setLocked(false);
      } catch {
        const windowMs = pendingWindow.current ?? 5000;
        setAnswerWindowMs(windowMs);
        setDeadlineAt(Date.now() + windowMs);
        setShowChoices(true);
        inFlight.current = false;
        setLocked(false);
      }
    },
    [finishRun],
  );

  const beginReading = useCallback(
    (id: string, nextQuestion: QuizQuestion, windowMs: number | null, readMs: number) => {
      clearReveal();
      pendingWindow.current = windowMs;
      setQuestion(nextQuestion);
      setAnswerWindowMs(windowMs);
      setDeadlineAt(null);
      setShowChoices(false);
      setLocked(true);
      if (readingTimer.current != null) window.clearTimeout(readingTimer.current);
      readingTimer.current = window.setTimeout(() => {
        readingTimer.current = null;
        void armAnswers(id);
      }, Math.max(0, readMs));
    },
    [armAnswers],
  );

  const start = async () => {
    setError(null);
    clearReveal();
    inFlight.current = false;
    await ensureGuest();
    try {
      const res = await api.startSolo({
        mode,
        locale,
        ...(mode === 'solo_timed' ? { durationSec } : {}),
      });
      setActiveMode(mode);
      setRunId(res.runId);
      setEndsAt(res.endsAt);
      setScore(res.score);
      setStreak(res.streak);
      setBestStreak(0);
      setCorrect(res.correctCount ?? 0);
      setWrong(res.wrong ?? 0);
      setTimeouts(res.timeouts ?? 0);
      setLivesLeft(res.livesLeft);
      setDailyTotal(res.dailyTotal ?? null);
      setDailyIndex(res.dailyIndex ?? null);
      setOnboarding(Boolean(res.onboarding));
      if (res.durationSec) setDurationSec(res.durationSec);
      if (!res.question) {
        setPhase('ended');
        return;
      }
      setPhase('playing');
      beginReading(
        res.runId,
        res.question,
        res.answerWindowMs ?? null,
        res.readingMs ?? readingMsForPrompt(res.question.prompt),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start');
      if (mode === 'solo_daily') setDailyPlayed(true);
    }
  };

  const submit = useCallback(
    async (choiceIndex: number | null) => {
      if (!runId || !question || !showChoices || locked || inFlight.current) return;
      inFlight.current = true;
      setLocked(true);
      setSelected(choiceIndex);
      setDeadlineAt(null);
      try {
        const res = await api.answerSolo(runId, {
          questionId: question.id,
          choiceIndex,
        });
        setScore(res.score);
        setStreak(res.streak);
        setBestStreak(res.bestStreak);
        setCorrect(res.correctCount ?? 0);
        setWrong(res.wrong ?? 0);
        setTimeouts(res.timeouts ?? 0);
        setLivesLeft(res.livesLeft);
        setDailyTotal(res.dailyTotal ?? null);
        setDailyIndex(res.dailyIndex ?? null);
        setHighlightCorrect(res.correctChoiceIndex);

        const wait = Math.max(300, res.revealMs ?? DEFAULT_REVEAL_MS);
        if (revealTimer.current != null) window.clearTimeout(revealTimer.current);

        const pendingQuestion = res.question;
        const pendingWin = res.answerWindowMs ?? null;
        const pendingEnded = res.ended === true;
        const readMs =
          res.readingMs ??
          (pendingQuestion ? readingMsForPrompt(pendingQuestion.prompt) : 1200);

        revealTimer.current = window.setTimeout(() => {
          revealTimer.current = null;
          if (pendingEnded || !pendingQuestion) {
            finishRun();
            return;
          }
          beginReading(runId, pendingQuestion, pendingWin, readMs);
        }, wait);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Answer failed');
        clearReveal();
        inFlight.current = false;
        setLocked(false);
      }
    },
    [runId, question, showChoices, locked, finishRun, beginReading],
  );

  useEffect(() => {
    if (phase !== 'playing' || !showChoices || !deadlineAt || locked || inFlight.current) {
      return;
    }
    const ms = deadlineAt - Date.now();
    if (ms <= 0) {
      void submit(null);
      return;
    }
    const timer = window.setTimeout(() => void submit(null), ms);
    return () => window.clearTimeout(timer);
  }, [phase, deadlineAt, question?.id, locked, showChoices, submit]);

  const matchClock =
    endsAt != null ? Math.max(0, Math.ceil((endsAt - now) / 1000)) : null;
  const bankClock =
    activeMode === 'solo_timebank' && deadlineAt != null && showChoices
      ? Math.max(0, Math.ceil((deadlineAt - now) / 1000))
      : null;

  if (phase === 'menu') {
    return (
      <section className="panel">
        <h1>{t('soloTitle')}</h1>
        <p className="muted">{t('soloIntro')}</p>
        {isFirstRun ? <p className="muted">{t('soloOnboardingHint')}</p> : null}
        {error ? <p className="error">{error}</p> : null}
        <div className="mode-grid" role="radiogroup" aria-label={t('soloTitle')}>
          {(
            [
              ['solo_timed', 'soloTimed', 'soloTimedDesc'],
              ['solo_lives', 'soloLives', 'soloLivesDesc'],
              ['solo_timebank', 'soloTimeBank', 'soloTimeBankDesc'],
              ['solo_daily', 'soloDaily', 'soloDailyDesc'],
            ] as const
          ).map(([id, titleKey, descKey]) => {
            const selectedMode = mode === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selectedMode}
                className={`mode-card${selectedMode ? ' selected' : ''}`}
                onClick={() => setMode(id)}
              >
                <strong>{t(titleKey)}</strong>
                <span>{t(descKey)}</span>
              </button>
            );
          })}
        </div>
        {mode === 'solo_timed' ? (
          <div className="duration-row">
            {TIMED_DURATIONS_SEC.map((d) => (
              <button
                key={d}
                type="button"
                className={durationSec === d ? 'btn primary' : 'btn'}
                onClick={() => setDurationSec(d)}
              >
                {d}
                {t('soloSec')}
              </button>
            ))}
          </div>
        ) : null}
        {mode === 'solo_timebank' ? (
          <p className="muted">{t('soloTimeBankRules')}</p>
        ) : null}
        {mode === 'solo_daily' ? (
          <p className="muted">
            {dailyPlayed ? t('soloDailyPlayed') : t('soloDailyRules')}
          </p>
        ) : null}
        <p className="muted">{t('soloReadingRules')}</p>
        {mode === 'solo_daily' && dailyPlayed ? (
          <Link className="btn primary" to="/leaderboard?game=solo_daily">
            {t('soloViewBoard')}
          </Link>
        ) : (
          <button type="button" className="btn primary" onClick={() => void start()}>
            {t('soloStart')}
          </button>
        )}
      </section>
    );
  }

  if (phase === 'ended') {
    const isLives = activeMode === 'solo_lives';
    return (
      <section className="panel end-panel">
        <h1>{t('soloEnded')}</h1>
        {isLives ? (
          <>
            <p className="score-big">{bestStreak}</p>
            <p className="muted">
              {t('soloBestStreak')} · {t('soloScore')} {score}
            </p>
          </>
        ) : (
          <>
            <p className="score-big">{score}</p>
            <p className="muted">
              {t('soloBestStreak')} {bestStreak}
            </p>
          </>
        )}
        <p className="muted end-breakdown">
          {t('soloCorrect')} {correct} · {t('soloWrong')} {wrong} · {t('soloTimeouts')}{' '}
          {timeouts}
        </p>
        {onboarding ? <p className="muted">{t('soloOnboardingHint')}</p> : null}
        <ShareRunCard
          stats={{
            mode: activeMode,
            score,
            bestStreak,
            correct,
            wrong,
            timeouts,
            durationSec: activeMode === 'solo_timed' ? durationSec : null,
          }}
        />
        <div className="cta-row">
          <button type="button" className="btn primary" onClick={() => setPhase('menu')}>
            {t('soloPlayAgain')}
          </button>
          <Link
            className="btn"
            to={
              activeMode === 'solo_daily'
                ? '/leaderboard?game=solo_daily'
                : `/leaderboard?game=${activeMode}`
            }
          >
            {t('soloViewBoard')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="panel play-panel">
      <div className="hud">
        <div className="hud-stats">
          <span>
            {t('soloScore')} {score}
          </span>
          <span className={streak >= 3 ? 'on-fire' : ''}>
            {t('soloStreak')} {streak}
          </span>
          {livesLeft != null ? (
            <span>
              {t('soloLivesLabel')} {livesLeft}
            </span>
          ) : null}
          {matchClock != null ? (
            <span>
              {t('soloMatch')} {matchClock}s
            </span>
          ) : null}
          {bankClock != null ? (
            <span className={bankClock <= 10 ? 'on-fire' : ''}>
              {t('soloBank')} {bankClock}s
            </span>
          ) : null}
          {activeMode === 'solo_daily' && dailyTotal != null && dailyIndex != null ? (
            <span>
              {t('soloDailyProgress', {
                n: Math.min(dailyIndex, dailyTotal),
                total: dailyTotal,
              })}
            </span>
          ) : null}
        </div>
      </div>
      {error ? <p className="error">{error}</p> : null}
      {question ? (
        <QuizBoard
          key={question.id}
          question={question}
          deadlineAt={deadlineAt}
          windowMs={answerWindowMs}
          showChoices={showChoices}
          disabled={locked || !showChoices}
          selected={selected}
          highlightCorrect={highlightCorrect}
          onAnswer={(i) => void submit(i)}
        />
      ) : null}
    </section>
  );
}
